import { useContext, useCallback, useMemo } from "react";
import { FilterGroupContext } from "./internal/FilterGroupContext";
import { Filter } from "./internal/types";
import {
  ToolbarItemWrapper,
  ToolbarItemWrapperProps,
} from "../internal/ToolbarItemWrapper";
import { ToolbarButtonProps } from "../ToolbarButton";

import styles from "./Filters.module.scss";

/**
 * Props for the FilterItemWrapper component
 */
type FilterItemWrapperProps = {
  /**
   * Optional filter object. If provided, visibility changes will be tracked in FilterGroupContext.
   * If not provided, only basic visibility tracking will be used.
   */
  filter?: Filter;
  /** The content to render inside the wrapper */
  children: React.ReactNode;
};

/**
 * FilterItemWrapper extends ToolbarItemWrapper to handle filter-specific visibility.
 * Uses FilterGroupContext to track hidden filters instead of overflow menu.
 * Built into FilterButton and other filter components for consistent overflow handling.
 *
 * Features:
 * - Wraps filter controls for consistent overflow and visibility handling
 * - Integrates with FilterGroupContext to track hidden filters
 */
export const FilterItemWrapper = ({
  filter,
  children,
}: FilterItemWrapperProps) => {
  const { addHiddenFilter, removeHiddenFilter, filterGroupRef } =
    useContext(FilterGroupContext);

  const handleVisibilityChange = useCallback(
    (isVisible: boolean) => {
      // Only track in FilterGroupContext if a filter is provided
      if (filter) {
        if (isVisible) {
          removeHiddenFilter?.(filter);
        } else {
          addHiddenFilter?.(filter);
        }
      }
    },
    [filter, addHiddenFilter, removeHiddenFilter],
  );

  const props: ToolbarItemWrapperProps = useMemo(
    () => ({
      item: {
        itemType: "button",
        itemProps: {
          children: null,
          "aria-hidden": true,
        } as ToolbarButtonProps,
      },
      children,
      className: styles["filter-item"],
      observerRoot: filterGroupRef.current,
      onVisibilityChange: handleVisibilityChange,
      rootMargin: "0px -80px 0px 0px",
    }),
    [children, filterGroupRef, handleVisibilityChange],
  );

  return <ToolbarItemWrapper {...props} />;
};
